<template>
  <div
    v-if="details"
    class="flex flex-col gap-5 text-base @container"
    :class="allowOverflow && 'overflow-x-auto'"
  >
    <!-- Container that changes from column to row based on container size -->
    <div class="sight-data-tables flex flex-col gap-5 @md:flex-row @md:gap-0">
      <!-- Main sight data table -->
      <SightDataTableComponent
        v-if="tableDataRows?.length"
        :columns="columns"
        :data-rows="tableDataRows"
        :min-width="minWidth"
        :border="border"
        class="@md:flex-[3]"
      />

      <!-- Prism data table -->
      <SightDataTableComponent
        v-if="prismDataRows?.length"
        :columns="prismColumns"
        :data-rows="prismDataRows"
        :min-width="minWidth"
        :border="border"
        :hide-eye-column-on-desktop="true"
        class="prism-table @md:flex-[2]"
      />
    </div>

    <!-- PD values table -->
    <div v-if="pdValues">
      <table class="w-full table-fixed">
        <tbody>
          <tr
            v-for="[eye, value] in pdValues"
            :key="`row-${eye}`"
            :data-testid="`pd-${eye}`"
          >
            <td class="w-[17ch]">
              <span class="flex w-full justify-between text-[15px] leading-5">
                {{
                  $t(`rx.data_table.${eye === 'left' ? 'pd_left' : 'pd_right'}`)
                }}
              </span>
            </td>
            <td>{{ Number.isNaN(value) ? '-' : value }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <div v-else class="flex gap-5 rounded-lg bg-gray-350 p-3">
    <div class="flex flex-col justify-start">
      <div class="rounded-full bg-white p-2">
        <LazyIconFielmannEye class="size-10" />
      </div>
    </div>
    <span class="flex flex-col justify-center pr-5">
      {{ $t('rx.data_table.no_table_text') }}
    </span>
  </div>
</template>
<script setup lang="ts">
import type { DdoaProduct } from '~/types/ddoa'

const props = defineProps({
  details: {
    type: Object as PropType<
      CorrectionalPrescriptionValues | DdoaProduct['prescriptionValues']
    >,
    default: undefined,
  },
  allowOverflow: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  minWidth: {
    type: String as PropType<string>,
    default: '',
  },
  skipEmpty: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  border: {
    type: String as PropType<string>,
    default: 'border-y border-[#D7DDE3]',
  },
})

const $currentShop = useCurrentShop()

const tableData = computed(() => {
  if (props.details?.left && isRxPrescriptionValue(props.details.left)) {
    const formattedValues = formatSightSpecificationForDisplay(
      props.details as RxPrescriptionValues,
      $currentShop.value,
      true,
    )
    const {
      pd: leftPd,
      addition: leftAddition,
      prism: leftPrism,
      prismBase: leftPrismBase,
      ...left
    } = formattedValues.left || {}
    const {
      pd: rightPd,
      addition: rightAddition,
      prism: rightPrism,
      prismBase: rightPrismBase,
      ...right
    } = formattedValues.right || {}

    const prismLeft = removeEmpty({
      addition: leftAddition,
      prism: leftPrism,
      prismBase: leftPrismBase,
    })

    const prismRight = removeEmpty({
      addition: rightAddition,
      prism: rightPrism,
      prismBase: rightPrismBase,
    })

    const isVarifocal =
      Object.keys(prismLeft).length || Object.keys(prismRight).length

    return {
      rows: { right, left },
      prismRows: isVarifocal
        ? { right: prismRight, left: prismLeft }
        : undefined,
      pdValues: {
        right: rightPd,
        left: leftPd,
      },
    }
  } else {
    const formattedValues = formatSightSpecificationForDisplay(
      props.details as Partial<ContactLensValues>,
      $currentShop.value,
      true,
    )
    const displayValues = {
      right: formattedValues.right || {},
      left: formattedValues.left || {},
    }
    const orderedVals = {
      right: {} as typeof displayValues.right,
      left: {} as typeof displayValues.left,
    }
    contactLensProps.forEach((contactLensProp) => {
      if (
        contactLensProp !== 'diameter' &&
        (displayValues.left[contactLensProp] ||
          displayValues.right[contactLensProp])
      ) {
        orderedVals.left[contactLensProp] =
          displayValues.left[contactLensProp] ?? '-'
        orderedVals.right[contactLensProp] =
          displayValues.right[contactLensProp] ?? '-'
      }
    })

    return { rows: orderedVals }
  }
})

const tableDataRows = computed(() => {
  if (tableData?.value?.rows && props.skipEmpty) {
    return Object.entries(tableData.value.rows).filter(
      ([key]) => key in (props?.details ?? {}),
    )
  } else if (tableData?.value?.rows) {
    return Object.entries(tableData.value.rows)
  }
  return undefined
})

const columns = computed(() => {
  if (tableData.value?.rows?.left) {
    return Object.keys(tableData.value.rows.left)
  }
  return undefined
})

const prismDataRows = computed(() => {
  if (tableData?.value?.prismRows) {
    return Object.entries(tableData.value.prismRows).filter(
      ([key]) => key in (props?.details ?? {}),
    )
  }
  return []
})

const prismColumns = computed(() => {
  if (tableData.value?.prismRows?.left) {
    return Object.keys(tableData.value.prismRows.left)
  }
  return undefined
})

const pdValues = computed(() => {
  if (tableData?.value?.pdValues) {
    return Object.entries(tableData.value.pdValues)
  }
  return undefined
})
</script>
